import { useNavigate } from 'react-router'

import { useAuthToken } from '@surglogs-app/auth'
import { useHandler } from '@surglogs-app/hooks'
import {
  checkErrorResponseStatus,
  useAccessManagementAccountPasswordPostMutation,
  useAuthTokenRevokePostMutation,
} from '@surglogs-app/network'
import { getAuthLogoutRoute } from '@surglogs-app/routing'
import { Config } from '@surglogs-app/types'

import { useEventCapture } from '../../../analytics/src'

import { useAccountPasswordUpdateFailureAlert } from './useAccountPasswordUpdateFailureAlert'
import { useReCaptchaToken } from './useReCaptchaToken'

export const useAccountEditPasswordSubmitHandler = ({
  config,
}: {
  hideModal: () => void
  config: Pick<Config, 'reCaptchaKey'>
}) => {
  const [getReCaptchaToken] = useReCaptchaToken({ config })
  const { captureEvent } = useEventCapture()
  const navigate = useNavigate()
  const { token, refreshToken, clearAuthData } = useAuthToken()

  const mutation = useAccessManagementAccountPasswordPostMutation(
    {},
    {
      opts: {
        throwOnError: false,
      },
    },
  )

  const { mutateAsync: revokeJwtTokenMutateAsync } =
    useAuthTokenRevokePostMutation()

  const { showAccountPasswordUpdateFailureAlert } =
    useAccountPasswordUpdateFailureAlert()

  const onSubmit = useHandler(async (values) => {
    try {
      const reCaptchaToken = await getReCaptchaToken()

      await mutation.mutateAsync({
        data: {
          oldpassword: values.password,
          password: values.newPassword,
          ...(reCaptchaToken != null ? { captcha_token: reCaptchaToken } : {}),
        },
      })

      await captureEvent('account_password_changed')

      navigate(getAuthLogoutRoute('password_changed'))
    } catch (error) {
      if (checkErrorResponseStatus(error, 429)) {
        clearAuthData()

        if (token) {
          await revokeJwtTokenMutateAsync({
            access_token: token,
            refresh_token: refreshToken,
          })
        }
      }

      showAccountPasswordUpdateFailureAlert({ error })
    }
  })

  return [onSubmit, mutation] as const
}
